/**
 * Team Preferences Service
 *
 * Reads/writes the Appwrite Team preferences for the current user's team.
 * The team = one client_id (Brainiacs Corporate ID), shared by all members
 * on that account. Only team admins / superadmins may edit (gated in the UI).
 *
 * The Team prefs shape (verified in prod):
 *   { language, company_website, timezone, country_code, currency,
 *     billing_email, lvl1_id, mod_leads, mod_outreach, mod_sales,
 *     mod_workers, mod_nexus, mod_chat }
 * Module flags are stored as STRING "true"/"false" in Appwrite — this service
 * normalizes them to booleans on read and back to strings on write.
 *
 * TODO(backend): create an Appwrite Function action that (a) gates writes to
 * team admins and (b) handles module purchase/activation (Stripe). The client
 * currently trusts the teamadmin label and writes prefs directly.
 */

import { backendProvider } from './backend';
import {
  DEFAULT_COMPANY_LOGO_FILE_ID,
  getCompanyLogoUrlFromTeamPreferences,
  validateCompanyLogoFile,
} from '../utils/companyLogo';
import { normalizeBrandHexColour } from '../utils/brandColour';
import { teamBrandingService } from './backend/appwrite/teamBrandingService';

/** A product module the team can activate. Maps to a `mod_*` team-pref flag. */
export interface ModuleCatalogEntry {
  /** Team-prefs key without the `mod_` prefix, e.g. `mod_leads` → `leads`. */
  field: 'leads' | 'outreach' | 'sales' | 'workers' | 'nexus' | 'chat' | 'goals';
  /** The raw prefs key, e.g. `mod_leads`. */
  prefKey: string;
  label: string;
  /** Iconify icon. */
  icon: string;
  /** Short operating-model label shown above the module name. */
  categoryLabel: string;
  description: string;
  /** Human-readable pricing/contract note. Omitted for included sub-features. */
  priceNote?: string;
  /**
   * Sub-features render nested under their parent product. Outreach and
   * Campaign Assistant are FEATURES of Leads Intelligence — free to activate
   * (request-only), never contracted.
   */
  subFeatureOf?: ModuleField;
  /**
   * How the user requests this module:
   *  - 'outreach_setup' → the dedicated Outreach infrastructure form
   *    (weekly target + channels, SupportRequestModal unlock_outreach mode)
   *  - 'activation'     → free activation request (contract_module support action)
   *  - 'contract'       → contracting request via support (contract_module)
   */
  requestMode: 'outreach_setup' | 'activation' | 'contract';
}

/**
 * Single source of truth for the modules catalog. The UI renders from this;
 * the team record stores booleans derived from the `mod_*` prefs.
 */
export const MODULE_CATALOG: ModuleCatalogEntry[] = [
  { field: 'leads', prefKey: 'mod_leads', label: 'Leads Intelligence', icon: 'bx:user-plus', categoryLabel: 'Lead data foundation', description: 'Generate, enrich and score leads against your ICP.', priceNote: 'Pay-As-You-Go', requestMode: 'contract' },
  { field: 'outreach', prefKey: 'mod_outreach', label: 'SDR Workspace', icon: 'bx:user-voice', categoryLabel: 'Manual outreach control', description: 'Review lead intelligence, manage statuses and sequences, and run outreach manually.', subFeatureOf: 'leads', requestMode: 'activation' },
  { field: 'sales', prefKey: 'mod_sales', label: 'Sales Intelligence', icon: 'bx:line-chart', categoryLabel: 'AI-managed omnichannel infrastructure', description: 'AI operates sending, follow-ups, replies and delivery across every connected outreach channel.', priceNote: 'Pay-As-You-Go', requestMode: 'outreach_setup' },
  { field: 'workers', prefKey: 'mod_workers', label: 'AI Workers', icon: 'bx:bot', categoryLabel: 'Autonomous operations', description: 'Deploy autonomous workers for recurring tasks and orders.', priceNote: 'Pay-As-You-Go', requestMode: 'contract' },
  { field: 'nexus', prefKey: 'mod_nexus', label: 'Nexus', icon: 'bx:network-chart', categoryLabel: 'Personal AI server', description: 'Open the Nexus onboarding and payment experience for your private AI server.', priceNote: '€49/month + usage', requestMode: 'contract' },
  { field: 'chat', prefKey: 'mod_chat', label: 'Chat Campaign AI Assistant', icon: 'bx:message-dots', categoryLabel: 'In-app campaign copilot', description: 'Chat with AI across campaigns and leads without leaving your workspace.', subFeatureOf: 'leads', requestMode: 'activation' },
  { field: 'goals', prefKey: 'mod_goals', label: 'Goals Intelligence', icon: 'bx:target-lock', categoryLabel: 'Next-gen OKRs — AI + humans teaming', description: 'Mission, vision, values, long-term strategy, yearly goals, quarterly goals and weekly focus — one operating cadence where humans and AI are teammates and shared resources.', requestMode: 'contract' },
];

export type ModuleField = ModuleCatalogEntry['field'];

export interface TeamPreferences {
  team_name?: string;
  language?: string;
  company_website?: string;
  timezone?: string;
  /** Full country NAME from geo_countries (e.g. "Spain"). */
  country?: string;
  country_code?: string;
  /** State/province name (e.g. "Barcelona") — refines the timezone. */
  state?: string;
  currency?: string;
  billing_email?: string;
  /** Access control: active | blocked | invited. */
  status?: string;
  lvl1_id?: string;
  /**
   * Incremental seller registry for the outreach context swap — stored in the
   * TEAM pref `seller_ids` as a CSV of `lvl1_id§SellerName` references
   * (e.g. "69166§Opentix,70209§Red Hat"). Sellers are NOT Appwrite teams;
   * the backend reads this pref at create_campaign to swap the seller context
   * for the campaign. Appending a seller = read current value + add the new
   * reference (see SellerSelector).
   */
  seller_ids?: string | null;
  logo_url?: string;
  brand_primary_colour?: string;
  /** Partner plan flag (raw pref `partner`, stored as string — normalized here). */
  partner?: boolean;
  /** Derived UI state; not persisted in Appwrite Team preferences. */
  company_logo_is_default?: boolean;
  /** Module activation flags, normalized to booleans. */
  modules: Record<ModuleField, boolean>;
}

/** Module flags default OFF when the team hasn't set them. */
const DEFAULT_MODULES: Record<ModuleField, boolean> = {
  leads: false,
  outreach: false,
  sales: false,
  workers: false,
  nexus: false,
  chat: false,
  goals: false,
};

const parseBool = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

/** Normalize raw Appwrite team prefs into the typed shape. */
const normalize = (raw: Record<string, any> | null | undefined): TeamPreferences => {
  const safe = raw || {};
  const modules = { ...DEFAULT_MODULES };
  MODULE_CATALOG.forEach((m) => {
    modules[m.field] = parseBool(safe[m.prefKey]);
  });
  return {
    team_name: safe.team_name,
    language: safe.language,
    company_website: safe.company_website,
    timezone: safe.timezone,
    country: safe.country,
    country_code: safe.country_code,
    state: safe.state,
    currency: safe.currency,
    billing_email: safe.billing_email,
    status: safe.status,
    lvl1_id: safe.lvl1_id,
    // getTeamPreferences already decodes the storage "null" sentinel to real
    // null via decodeStoragePrefs; only guard against the literal/empty forms.
    seller_ids: typeof safe.seller_ids === 'string' && safe.seller_ids.trim()
      ? safe.seller_ids
      : null,
    logo_url: getCompanyLogoUrlFromTeamPreferences(safe),
    brand_primary_colour: normalizeBrandHexColour(safe.brand_primary_colour) || undefined,
    partner: safe.partner === true || safe.partner === 'true',
    modules,
  };
};

export class TeamPreferencesService {
  /**
   * Get the current user's team preferences (typed). Returns a normalized
   * empty shape if the user has no team.
   */
  async getPreferences(): Promise<TeamPreferences> {
    const raw = await backendProvider.auth.getTeamPreferences();
    const preferences = normalize(raw);

    if (preferences.logo_url) {
      return { ...preferences, company_logo_is_default: false };
    }

    return {
      ...preferences,
      logo_url: backendProvider.storage.getTeamLogoViewUrl(DEFAULT_COMPANY_LOGO_FILE_ID),
      company_logo_is_default: true,
    };
  }

  /**
   * Update the team preferences (merge — only the provided keys are written).
   * Module flags are accepted as booleans and serialized to "true"/"false"
   * strings to match the existing Appwrite convention.
   */
  async updatePreferences(updates: Partial<Omit<TeamPreferences, 'modules'>> & { modules?: Partial<Record<ModuleField, boolean>> }): Promise<boolean> {
    const payload: Record<string, any> = {};
    (['language', 'company_website', 'timezone', 'country', 'country_code', 'state', 'currency', 'billing_email', 'lvl1_id', 'seller_ids', 'logo_url', 'brand_primary_colour'] as const).forEach((key) => {
      if (updates[key] !== undefined) payload[key] = updates[key];
    });
    if (updates.modules) {
      MODULE_CATALOG.forEach((m) => {
        if (updates.modules![m.field] !== undefined) {
          payload[m.prefKey] = String(updates.modules![m.field]);
        }
      });
    }
    return backendProvider.auth.updateTeamPreferences(payload);
  }

  async updateBrandPrimaryColour(colour: string): Promise<string> {
    const normalizedColour = normalizeBrandHexColour(colour);
    if (!normalizedColour) {
      throw new Error('Use a valid six-digit hexadecimal brand colour.');
    }

    const teamId = await backendProvider.auth.getTeamId();
    if (!teamId) throw new Error('No Appwrite team is available for this account.');

    const saved = await teamBrandingService.updatePrimaryColour(teamId, normalizedColour);
    return saved.brand_primary_colour;
  }

  /**
   * Replace the current team's company logo. The upload uses the Team ID as
   * the Storage file ID, so it overwrites the previous logo in place — the
   * client-logos bucket always holds exactly one logo per team.
   */
  async updateCompanyLogo(file: File): Promise<{ fileId: string; url: string }> {
    const validationError = await validateCompanyLogoFile(file);
    if (validationError) throw new Error(validationError);

    const teamId = await backendProvider.auth.getTeamId();
    if (!teamId) throw new Error('No Appwrite team is available for this account.');

    // Upload with teamId as the deterministic file ID — only one logo per team
    // in the bucket, re-upload replaces the previous file.
    const fileId = teamId;
    await backendProvider.storage.uploadTeamLogo(teamId, file);
    const activeLogo = await teamBrandingService.updateLogo(teamId, fileId);

    return { fileId, url: activeLogo.logo_url };
  }
}

/** Singleton instance. */
export const teamPreferencesService = new TeamPreferencesService();
